/* Yükleme öncesi denetim.

   Türev üretimi tarayıcıda olduğu için kabul edilen biçim kümesi
   tarayıcının çözebildikleriyle sınırlıdır. Ölçüldü (tools/harness/probe.html):

     JPEG 102 MP / 81 MB  → 592 ms'de çözülüyor, sorunsuz
     CR3, NEF, ARW, DNG   → InvalidStateError, hiç çözülmüyor
     TIFF                 → Chrome'da çözülmüyor

   Bu yüzden dosya, çözmeye kalkışmadan önce burada elenir; yönetici
   "InvalidStateError" yerine ne yapması gerektiğini söyleyen bir ileti
   görür. */

/** Tarayıcının güvenilir biçimde çözdüğü tipler. */
export const ACCEPTED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'] as const;

/** Ham makine dosyaları — tarayıcı çözemez, ayrı ele alınır. */
export const RAW_EXTENSIONS = [
  'cr2', 'cr3', 'crw',        // Canon
  'nef', 'nrw',               // Nikon
  'arw', 'srf', 'sr2',        // Sony
  'raf',                      // Fujifilm
  'orf',                      // Olympus
  'rw2',                      // Panasonic
  'pef',                      // Pentax
  'dng',                      // Adobe / evrensel
  '3fr', 'fff',               // Hasselblad
  'iiq'                       // Phase One
] as const;

/** Tarayıcının çözemediği ama ham da olmayan biçimler. */
export const UNSUPPORTED_EXTENSIONS = ['tif', 'tiff', 'psd', 'psb', 'heic', 'heif'] as const;

/** 102 MP'de bile çözme 600 ms; bu tavan güvenli tarafta duruyor. */
export const MAX_BYTES = 120 * 1024 * 1024;
export const MAX_MEGAPIXELS = 150;

export type RejectionCode = 'raw' | 'unsupported' | 'too-large' | 'empty' | 'not-image';

export type IntakeResult =
  | { ok: true; mime: string; ext: string }
  | { ok: false; code: RejectionCode; message: string };

const extOf = (name: string) => (name.split('.').pop() ?? '').toLowerCase();

const fmt = (bytes: number) =>
  bytes >= 1048576 ? `${(bytes / 1048576).toFixed(0)} MB` : `${Math.round(bytes / 1024)} KB`;

/**
 * Dosyayı çözmeye kalkışmadan önce eler.
 * Pahalı olan çözme adımıdır; bu denetim yalnızca ada, boyuta ve tipe bakar.
 */
export function inspect(file: { name: string; size: number; type: string }): IntakeResult {
  if (file.size === 0) {
    return { ok: false, code: 'empty', message: `"${file.name}" boş görünüyor.` };
  }

  const ext = extOf(file.name);

  if ((RAW_EXTENSIONS as readonly string[]).includes(ext)) {
    return {
      ok: false,
      code: 'raw',
      message:
        `"${file.name}" ham makine dosyası (.${ext}). Tarayıcılar ham dosyaları ` +
        `açamaz, bu yüzden türevler buradan üretilemiyor. Lightroom ya da ` +
        `Capture One içinden tam çözünürlükte JPEG olarak dışa aktarıp onu yükleyin.`
    };
  }

  if ((UNSUPPORTED_EXTENSIONS as readonly string[]).includes(ext)) {
    return {
      ok: false,
      code: 'unsupported',
      message:
        `"${file.name}" biçimi (.${ext}) tarayıcıda açılamıyor. ` +
        `JPEG olarak dışa aktarıp yükleyin.`
    };
  }

  if (!(ACCEPTED_MIME as readonly string[]).includes(file.type)) {
    return {
      ok: false,
      code: 'not-image',
      message: `"${file.name}" tanınan bir görsel değil. Kabul edilenler: JPEG, PNG, WebP, AVIF.`
    };
  }

  if (file.size > MAX_BYTES) {
    return {
      ok: false,
      code: 'too-large',
      message: `"${file.name}" ${fmt(file.size)} — üst sınır ${fmt(MAX_BYTES)}.`
    };
  }

  return { ok: true, mime: file.type, ext: ext || 'jpg' };
}

/** Çözdükten sonraki piksel denetimi — çok büyük kareler belleği zorlar. */
export function inspectDecoded(width: number, height: number): IntakeResult {
  const mp = (width * height) / 1_000_000;
  if (mp > MAX_MEGAPIXELS) {
    return {
      ok: false,
      code: 'too-large',
      message: `Kare ${mp.toFixed(0)} MP (${width}×${height}) — üst sınır ${MAX_MEGAPIXELS} MP.`
    };
  }
  return { ok: true, mime: 'image/jpeg', ext: 'jpg' };
}

/**
 * Eşzamanlı işlenecek dosya sayısı.
 *
 * Çözülmüş bitmap JS yığınında değil yerel bellekte durur; 100 MP bir kare
 * ham hâlde ~390 MB eder. Büyük dosyalarda paralelliği düşürmek, zayıf
 * makinelerde sekmenin çökmesini önler (plan §15 bellek riski).
 */
export function concurrencyFor(averageBytes: number): number {
  if (averageBytes > 40 * 1024 * 1024) return 1;
  if (averageBytes > 15 * 1024 * 1024) return 2;
  return 4;
}
